/**
 * Primary saved-address lookups for the sales-order form.
 *
 * Loads the customer's and site-group's saved addresses into the store and
 * resolves the four primary addresses the order form seeds from — the
 * customer and site-group primary shipping and billing addresses. The form
 * uses these to auto-populate a new order's shipping/billing fields from the
 * account's defaults.
 *
 * Contains:
 * - useOrderSaleAddresses — load + resolve the four primary addresses.
 */
import { useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { loadAddresses } from '@sophocles/features/warehouse/orders/api';
import { addressSlice } from '@sophocles/features/warehouse/orders/slice';

/**
 * @param {object}       scope
 * @param {number|null}  scope.liveCustomerId  - Currently selected customer id.
 * @param {number|null}  scope.liveSiteGroupId - Currently selected site-group id.
 * @returns {{
 *   customerPrimaryShipAddress: object|undefined,
 *   customerPrimaryBillAddress: object|undefined,
 *   siteGroupPrimaryShipAddress: object|undefined,
 *   siteGroupPrimaryBillAddress: object|undefined,
 * }}
 */
export function useOrderSaleAddresses({ liveCustomerId, liveSiteGroupId }) {
  const dispatch = useDispatch();

  useEffect(() => {
    if (liveCustomerId != null) {
      dispatch(loadAddresses({ customer: liveCustomerId }));
    }
  }, [liveCustomerId]);

  useEffect(() => {
    if (liveSiteGroupId != null) {
      dispatch(loadAddresses({ site_group: liveSiteGroupId }));
    }
  }, [liveSiteGroupId]);

  const addresses = useSelector(state => addressSlice.selectors.selectAllEntities(state));

  // Memoize address filtering to prevent unnecessary recalculations
  const availableAddresses = useMemo(
    () => addresses.filter(address => address.customer == liveCustomerId || address.site_group == liveSiteGroupId),
    [addresses, liveCustomerId, liveSiteGroupId],
  );

  // Memoize primary address lookups to prevent unnecessary recalculations
  const customerPrimaryShipAddress = useMemo(
    () =>
      availableAddresses.find(
        address =>
          address.customer != null &&
          address.customer == liveCustomerId &&
          !address.is_billing &&
          address.primary_address,
      ),
    [availableAddresses, liveCustomerId],
  );

  const customerPrimaryBillAddress = useMemo(
    () =>
      availableAddresses.find(
        address =>
          address.customer != null &&
          address.customer == liveCustomerId &&
          address.is_billing &&
          address.primary_address,
      ),
    [availableAddresses, liveCustomerId],
  );

  const siteGroupPrimaryShipAddress = useMemo(
    () =>
      availableAddresses.find(
        address =>
          address.site_group != null &&
          address.site_group == liveSiteGroupId &&
          !address.is_billing &&
          address.primary_address,
      ),
    [availableAddresses, liveSiteGroupId],
  );

  const siteGroupPrimaryBillAddress = useMemo(
    () =>
      availableAddresses.find(
        address =>
          address.site_group != null &&
          address.site_group == liveSiteGroupId &&
          address.is_billing &&
          address.primary_address,
      ),
    [availableAddresses, liveSiteGroupId],
  );

  return {
    customerPrimaryShipAddress,
    customerPrimaryBillAddress,
    siteGroupPrimaryShipAddress,
    siteGroupPrimaryBillAddress,
  };
}
